import { Img } from '@shared/Frontend/components/img'
import { Nav } from '@shared/Frontend/components/navItems'
import { useEventListener } from '@shared/Frontend/hooks/useEventListener'
import { pushDatalayer } from '@shared/Frontend/seo/datalayer'
import { setCookie } from '@shared/Frontend/utils/cookies'
import { InternalLink, Link } from '@shared/Frontend/utils/link'
import { PageLocalizationLinks } from '@shared/Frontend/utils/routes.shared'
import { getSite } from '@shared/Frontend/utils/site'
import { translate } from '@shared/Frontend/utils/translate'
import i18next from 'i18next'
import React, { ReactNode } from 'react'
import { RootView } from '../../../libraries/root/root.template'
import { PageState } from '../../../page/page.state'
import { SVGIcon } from '@shared/Frontend/utils/svg'

const site = getSite()

export const SiteHeader: RootView['SiteHeader'] = ({ menuOpen, toggleMenu, pageKind, localizationLinks, children }) => (
  <>
    <SkipLinks />
    {site == 'ZEG' && (
      <div className='sh__intro'>
        <p className='ls-container ls-gutter text--md'>Platform voor gezondheidsprofessionals</p>
      </div>
    )}
    <header id="a11y-siteheader" className={`siteheader sh sh--${pageKind}`}>
      <div className={`sh__outer ${site == 'ZEG' ? 'ls-gutter' : ''}`}>
        <div className={`sh__primary ${site == 'ZEG' ? 'ls-container--l' : ''}`}>
          <div className="sh__inner ls-container ls-gutter">
            <InternalLink className="sh__logo" url={`/${i18next.language}`} title={translate('a11y:toHomePage', {}, 'To home')}>
              <span className="sr-text">{translate('a11y:toHomePage', {}, 'To home')}</span>
              <Img
                className="sh__logoimage"
                src={"/images/spa/logo.svg"}
                width={site == 'ZEG' ? "190" : "80"}
                height={site == 'ZEG' ? "48" : "112"}
                alt={site == 'ZEG' ? "Logo 'Zuivel en Gezondheid'" : i18next.language == 'en' ? "Logo 'Dutch Dairy Association'" : "Logo 'Nederlandse Zuivel Organisatie'"}
                loading="eager"
              />
            </InternalLink>

            <MenuToggler menuOpen={menuOpen} toggleMenu={toggleMenu} />

            <Menu pageKind={pageKind} menuOpen={menuOpen}>
              <ul className='nav__list text--md'>
                {children}
              </ul>

              <div className='nav--utilities'>
                <ul className='nav__list text--md'>
                  {site != 'ZEG' && (
                    <>
                      <LanguageSwitch languages={window.languages} localizationLinks={localizationLinks} />

                      <li className='nav__item'>
                        <Link className='nav__link nav__link--icon text--md' url='https://extranet.nzo.nl' external={true}>
                          <SVGIcon className="nav__linkicon" id="icon-login" size={24} />
                          <span className='nav__label'>{translate('common:header:login', {}, 'Login')}</span>
                        </Link>
                      </li>
                    </>
                  )}

                  <li className='nav__item nav__item--search'>
                    <Link className="nav__link nav__link--icon text--md" url={translate('common:header:searchUrl', {}, '/en/search')} external={false}>
                      <SVGIcon className="nav__linkicon" id="icon-search" size={24} />
                      <span className='nav__label'>{translate('common:header:searchA11Y', {}, 'Search')}</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </Menu>
          </div>
        </div>
      </div>
    </header>
  </>
)

const SkipLinks = () => (
  <nav aria-label="Skip links">
    <a href="#a11y-main" className="skiplink">
      {translate('a11y:jumpToContent', {}, 'Jump to content')}
    </a>
    <a href="#a11y-footer" className="skiplink">
      {translate('a11y:jumpToFooter', {}, 'Jump to footer')}
    </a>
  </nav>
)

const Menu = ({ pageKind, menuOpen, children }: { pageKind: PageState['kind']; menuOpen: boolean; children: ReactNode }) => (
  <Nav id="a11y-nav-main" navClassName="nav--main" ariaLabel={translate('common:header:a11y', {}, 'Primary')} menuOpen={menuOpen}>
    {children}
  </Nav>
)

const LanguageSwitch = ({
  languages,
  localizationLinks,
}: {
  languages: string[]
  localizationLinks?: PageLocalizationLinks
}) => {
  if (!languages) return <></>
  return (
    <>
      {languages.map((language) => (
        i18next.language == language ? <></> :
        <li className="nav__item" key={language}>
          <InternalLink
            url={getLanguageSwitchLink(language, localizationLinks)}
            onClick={() => {
              pushDatalayer({ category: 'Language Switch', action: 'click', language: language })
              setCookie('language', language)
            }}
            aria-current={i18next.language == language ? true : undefined}
            className="nav__link"
          >
            <LanguageLabel langCode={language} />
          </InternalLink>
        </li>
      ))}
    </>
  )
}

const displayNames = window.languages?.map((language) => ({
  [language]: new Intl.DisplayNames(language, { type: 'language' }).of(language),
}))

const LanguageLabel = ({ langCode }: { langCode: string }) => {
  return <span className='nav__label'>{langCode == 'en' ? 'English' : 'Nederlands'}</span>
  // return <abbr className="nav__label" title={displayNames.find((name) => name[langCode])?.[langCode]}>{langCode}</abbr>
}

export const MenuToggler = ({ menuOpen, toggleMenu }: { menuOpen: boolean; toggleMenu: (_: boolean) => void }) => {
  useEventListener(
    'keydown',
    (e): void => {
      if (e.key == 'Escape') toggleMenu(false)
    },
    { passive: true }
  )
  return (
    <div className="sh__toggler">
      <button
        className="sh__togglerbutton"
        onClick={(): void => {
          toggleMenu(!menuOpen),
          !menuOpen ? document.documentElement?.classList.add('nav--toggled') : document.documentElement?.classList.remove('nav--toggled')
        }}
        aria-controls="a11y-nav-main"
        aria-expanded={menuOpen}
      >
        <span className="sr-text">{`${menuOpen ? translate('a11y:closeMenu') : translate('a11y:openMenu')}`}</span>
        <div className='sh__togglericon'>
          <span className='sh__togglerline'></span>
          <span className='sh__togglerline'></span>
          <span className='sh__togglerline'></span>
          <span className='sh__togglerline'></span>
        </div>
      </button>
    </div>
  )
}

const getLanguageSwitchLink = (language: string, localizationLinks?: PageLocalizationLinks) => {
  const defaultSwitchLink = `/${language}`
  if (!localizationLinks) return defaultSwitchLink
  else if (i18next.language == language) return window.location.pathname
  else {
    const localizationLink = localizationLinks[language]
    if (localizationLink) return localizationLink
  }
  return defaultSwitchLink
}
